import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bike, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { AddressInput } from './AddressInput';

export function ClientData() {
  const { id: caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [useDeliveryAsPostal, setUseDeliveryAsPostal] = useState(false);
  const [addressDetails, setAddressDetails] = useState<{
    postal?: { address: string; lat?: number; lng?: number };
    delivery?: { address: string; lat?: number; lng?: number };
  }>({});
  const [availableBikes, setAvailableBikes] = useState<Array<any>>([]);
  const [selectedBikeId, setSelectedBikeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (caseId) {
      fetchCaseData();
      fetchAvailableBikes();
    }
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('cases')
        .select('*, bike:bikes(*)')
        .eq('id', caseId)
        .single();

      if (error) throw error;
      setCaseData(data);
      
      // Initialize address details
      if (data.postal_address || data.delivery_address) {
        setAddressDetails({
          postal: data.postal_address ? { 
            address: data.postal_address,
            lat: undefined,
            lng: undefined 
          } : undefined,
          delivery: data.delivery_address ? { 
            address: data.delivery_address,
            lat: data.delivery_coordinates?.lat,
            lng: data.delivery_coordinates?.lng
          } : undefined
        });
      }
    } catch (error) {
      console.error('Error fetching case:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableBikes = async () => {
    try {
      const { data, error } = await supabase
        .from('bikes')
        .select('*')
        .eq('status', 'available')
        .order('make', { ascending: true });

      if (error) throw error;
      setAvailableBikes(data || []);
    } catch (error) {
      console.error('Error fetching available bikes:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const updateData = {
        not_at_fault_name: caseData.not_at_fault_name,
        claim_number: caseData.claim_number,
        not_at_fault_insurance_company: caseData.not_at_fault_insurance_company,
        not_at_fault_rego: caseData.not_at_fault_rego,
        at_fault_name: caseData.at_fault_name,
        at_fault_policy_number: caseData.at_fault_policy_number,
        at_fault_insurance_company: caseData.at_fault_insurance_company,
        at_fault_rego: caseData.at_fault_rego,
        postal_address: useDeliveryAsPostal && addressDetails.delivery ? 
          addressDetails.delivery.address : 
          addressDetails.postal?.address,
        delivery_address: addressDetails.delivery?.address,
        delivery_coordinates: addressDetails.delivery ? {
          lat: addressDetails.delivery.lat,
          lng: addressDetails.delivery.lng
        } : null,
        bike_id: selectedBikeId || caseData.bike_id
      };

      const { error } = await supabase
        .from('cases')
        .update(updateData)
        .eq('id', caseId);

      if (error) throw error;

      // If a new bike is being assigned, set its status to delivered
      if (selectedBikeId && selectedBikeId !== caseData.bike_id) {
        const selectedBike = availableBikes.find(bike => bike.id === selectedBikeId);
        if (selectedBike) {
          const { error: bikeError } = await supabase
            .from('cases')
            .update({
              bike_id: selectedBikeId,
              daily_rate: selectedBike.rate_a,
              rental_start_date: new Date().toISOString(),
              status: 'bike_delivered'
            })
            .eq('id', caseId);
          if (bikeError) throw bikeError;
        }
      }

      // Navigate back to case management
      navigate('/case-management');
    } catch (error) {
      console.error('Error updating case:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCaseData((prev: any) => ({ ...prev, [name]: value }));
  };

  // Address selection is now handled directly in the onChange handlers

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Case not found</h3>
        <button 
          onClick={() => navigate('/case-management')}
          className="mt-4 px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors"
        >
          Back to Client Data
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6">
        <div className="mb-6 flex items-center">
          <button 
            onClick={() => navigate('/case-management')} 
            className="mr-4 flex items-center text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span>Back to Cases</span>
          </button>
          <h1 className="text-2xl font-bold">Client Data</h1>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Not at Fault Party */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Not at Fault Party</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="not_at_fault_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="not_at_fault_name"
                  name="not_at_fault_name"
                  value={caseData.not_at_fault_name || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="claim_number" className="block text-sm font-medium text-gray-700 mb-1">
                  Claim Number
                </label>
                <input
                  type="text"
                  id="claim_number"
                  name="claim_number"
                  value={caseData.claim_number || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="not_at_fault_insurance_company" className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance Company
                </label>
                <input
                  type="text"
                  id="not_at_fault_insurance_company"
                  name="not_at_fault_insurance_company"
                  value={caseData.not_at_fault_insurance_company || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="not_at_fault_rego" className="block text-sm font-medium text-gray-700 mb-1">
                  Registration
                </label>
                <input
                  type="text"
                  id="not_at_fault_rego"
                  name="not_at_fault_rego"
                  value={caseData.not_at_fault_rego || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
            </div>
          </div>

          {/* At Fault Party */}
          <div className="mb-6 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">At Fault Party</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="at_fault_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="at_fault_name"
                  name="at_fault_name"
                  value={caseData.at_fault_name || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="at_fault_policy_number" className="block text-sm font-medium text-gray-700 mb-1">
                  Policy Number
                </label>
                <input
                  type="text"
                  id="at_fault_policy_number"
                  name="at_fault_policy_number"
                  value={caseData.at_fault_policy_number || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="at_fault_insurance_company" className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance Company
                </label>
                <input
                  type="text"
                  id="at_fault_insurance_company"
                  name="at_fault_insurance_company"
                  value={caseData.at_fault_insurance_company || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
              <div>
                <label htmlFor="at_fault_rego" className="block text-sm font-medium text-gray-700 mb-1">
                  Registration
                </label>
                <input
                  type="text"
                  id="at_fault_rego"
                  name="at_fault_rego"
                  value={caseData.at_fault_rego || ''}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#6563ff] focus:border-[#6563ff]"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="mb-6 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Address
                </label>
                <AddressInput
                  value={addressDetails.delivery?.address || ''}
                  onChange={(address: string) => {
                    setAddressDetails((prev: any) => ({
                      ...prev,
                      delivery: { 
                        address: address,
                        lat: prev.delivery?.lat,
                        lng: prev.delivery?.lng
                      }
                    }));
                    
                    if (useDeliveryAsPostal) {
                      setAddressDetails((prev: any) => ({
                        ...prev,
                        postal: { 
                          address: address,
                          lat: undefined,
                          lng: undefined
                        }
                      }));
                    }
                  }}
                  placeholder="Enter delivery address..."
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="use_delivery_as_postal"
                  checked={useDeliveryAsPostal}
                  onChange={(e) => setUseDeliveryAsPostal(e.target.checked)}
                  className="h-4 w-4 text-[#6563ff] focus:ring-[#6563ff] border-gray-300 rounded"
                />
                <label htmlFor="use_delivery_as_postal" className="ml-2 block text-sm text-gray-700">
                  Use delivery address as postal address
                </label>
              </div>
              {!useDeliveryAsPostal && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Address
                  </label>
                  <AddressInput
                    value={addressDetails.postal?.address || ''}
                    onChange={(address: string) => {
                      setAddressDetails((prev: any) => ({
                        ...prev,
                        postal: { 
                          address: address,
                          lat: undefined,
                          lng: undefined
                        }
                      }));
                    }}
                    placeholder="Enter postal address..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Bike Assignment Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Bike Assignment</h3>
            
            {caseData.bike ? (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Currently Assigned Bike</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Make & Model</p>
                    <p className="font-medium">{caseData.bike.make} {caseData.bike.model}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Registration</p>
                    <p className="font-medium">{caseData.bike.rego_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Daily Rate</p>
                    <p className="font-medium">${caseData.bike.rate_a}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  {availableBikes.length > 0 ? (
                    availableBikes.map((bike: any) => (
                      <div 
                        key={bike.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedBikeId === bike.id 
                            ? 'border-[#6563ff] bg-[#6563ff]/5' 
                            : 'border-gray-200 hover:border-[#6563ff] hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedBikeId(bike.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <Bike className={`h-6 w-6 ${selectedBikeId === bike.id ? 'text-[#6563ff]' : 'text-gray-400'}`} />
                            <div>
                              <p className="font-medium text-gray-900">{bike.make} {bike.model}</p>
                              <p className="text-sm text-gray-500">Rego: {bike.rego_number}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Rate A</p>
                              <p className="font-medium text-gray-900">${bike.rate_a}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Rate B</p>
                              <p className="font-medium text-gray-900">${bike.rate_b}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No bikes available for assignment
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t mt-6">
            <button
              type="button"
              onClick={() => navigate('/case-management')}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
